import React, { useState, useEffect } from 'react';
import {Segmented, Space, Popover, Row, Menu, Typography, Tooltip} from 'antd';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import {
    ZoomOutMapRounded,
    UnfoldMoreRounded,
    CachedRounded,
    CalendarMonthOutlined,
    CameraEnhanceRounded,
    MoreVertRounded,
    CloseRounded,
    LightModeRounded,
    NightsStayRounded,
    TextRotateVerticalRounded,
    TextRotationNoneRounded,
    MessageRounded, SpeakerNotesOffRounded, BarChartRounded, StackedLineChartRounded
} from "@mui/icons-material";
import DateRange from '../../components/Inputs/DateRange';
import dayjs from "dayjs";
const ButtonIcon = ({label}) => {
  switch(label) {
    case '1Y':
    case '3Y':
    case '5Y':
      return null;
    case 'Max':
      return <CachedRounded />;
    case 'Fullscreen':
      return <ZoomOutMapRounded/>;
    default:
      return null;
  }
}

const ChartConfigButtons = ({chartConfig, setChartConfig, extra})=>{
  return (
      <Space>
          {extra}
          <ButtonGroup size={'small'}>
            <Tooltip title={'New Dark Mode'}>
              <Button variant={chartConfig.theme === 'light' ? 'outlined' : 'contained'}
                      onClick={() => setChartConfig({
                          ...chartConfig,
                          theme: chartConfig.theme === 'dark' ? 'light' : 'dark'
                      })}>
                  {chartConfig.theme === 'light' ?
                      <LightModeRounded /> :
                      <NightsStayRounded />}
              </Button>
            </Tooltip>
            <Tooltip
                title={chartConfig.legendDirection === 'vertical' ? "Switch to Horizontal Legend" : "Switch to Vertical Legend"}>
                <Button variant={'outlined'}
                        onClick={() => setChartConfig({
                            ...chartConfig,
                            legendDirection: chartConfig.legendDirection === 'vertical' ? 'horizontal' : 'vertical'
                        })}>
                    {chartConfig.legendDirection === 'vertical' ?
                        <TextRotateVerticalRounded/> : <TextRotationNoneRounded/>}
                </Button>
            </Tooltip>
            <Tooltip
                title={chartConfig.disableTooltip ? "Disable Tooltip" : "Enable Tooltip"}>
                <Button size={'small'} variant={'outlined'}
                        onClick={() => setChartConfig({
                            ...chartConfig,
                            disableTooltip: !chartConfig.disableTooltip
                        })}>
                    {!chartConfig.disableTooltip ? <MessageRounded/> :
                        <SpeakerNotesOffRounded/>}
                </Button>
            </Tooltip>
            <Tooltip title={chartConfig.view==='bar' ? "Switch Back to Line Chart" : "Switch to Bar Chart"}>

                <Button variant={'outlined'}
                        onClick={() => setChartConfig({...chartConfig, view: chartConfig.view==='stock'?'bar':'stock'})}
                        startIcon={chartConfig.view==='bar'?<BarChartRounded/>:<StackedLineChartRounded/>}
                >
                    {chartConfig.view==='bar' ? 'Bar' : 'Line'}
                </Button>
            </Tooltip>
          </ButtonGroup>
      </Space>
  )
}
const RangeSelector = ({ years, globalMin, globalMax, onYearSelected,
                         selectedRange, extra, extraMiddle, chartConfig, setChartConfig}) => {
  const [selectedOption, setSelectedOption] = useState('max');
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth >= 1200);
  const [isXlScreen, setIsXlScreen] = useState(window.innerWidth >= 1600);
  const [showDateRange, setShowDateRange] = useState(false);

  const buttonColor = 'primary';

  useEffect(() => {
    const handleResize = () => {
      setIsXlScreen(window.innerWidth >= 1600);
      setIsLargeScreen(window.innerWidth >= 1320);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSelection = (value) => {
    setSelectedOption(value);
    let start, end;
    switch (value) {
      case '1_y':
        start = new Date(new Date().setFullYear(new Date().getFullYear() - 1));
        start.setDate(start.getDate() - 1); // Subtract one additional day
        end = new Date();
        break;
      case '3_y':
        start = new Date(new Date().setFullYear(new Date().getFullYear() - 3));
        start.setDate(start.getDate() - 1); // Subtract one additional day
        end = new Date();
        break;
      case '5_y':
        start = new Date(new Date().setFullYear(new Date().getFullYear() - 5));
        start.setDate(start.getDate() - 1); // Subtract one additional day
        end = new Date();
        break;
      case 'max':
        start = new Date(globalMin);
        end = new Date(globalMax);
        break;
      default:
        start = new Date(value, 0, 1);
        end = new Date(value, 11, 31);
        break;
    }
    onYearSelected({start: start.getTime(), end: end.getTime()});
  };

  const options = [
    { label: 'Max', value: 'max', variant: 'text' },
    { label: '1 Year', value: '1_y', variant: 'text' },
    { label: '3 Year', value: '3_y', variant: 'text' },
    { label: '5 Year', value: '5_y', variant: 'text' },
  ];


  const menu = (
    <Menu onClick={(e) => handleSelection(e.key)}>
      {years.map(year => (
        <Menu.Item key={year} style={{borderBottom: '1px solid #ddd', padding: '0px 10px', margin: 0}}>
          {year}
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Row justify={'space-between'}>
      <Space>
          {!showDateRange && selectedRange.start && selectedRange.end ? (
                <Button size={'small'} color={'primary'} variant={'text'} sx={{borderBottom: '1px solid #000', borderRadius:0}} onClick={()=>setShowDateRange(true)}>
                    <span className={'text-gray-600 font-semibold'}>
                        {new Intl.DateTimeFormat('en-US', 'yyyy-mm-dd').format(selectedRange.start)} - {new Intl.DateTimeFormat('en-US', 'yyyy-mm-dd').format(selectedRange.end)}
                    </span>
                </Button>
            ) : null}
          {isXlScreen && (
          <ButtonGroup
            size={'small'}
            color={'inherit'}
            exclusive
            variant={'outlined'}
            value={selectedOption}
          >
            {showDateRange && (
                <Space>
                  <Button  variant={'text'} color={'error'} onClick={()=>setShowDateRange(false)}>
                    <CloseRounded/>
                  </Button>
                  <DateRange
                    className={'small'}
                    startDate={selectedRange.start }
                    endDate={selectedRange.end}
                    minDate={globalMin}
                    maxDate={globalMax}
                    onDateRangeChange={(start, end) => onYearSelected({start, end})}
                  />
                </Space>
            )}
            {options.map(option => (
              <Button
                onClick={() => handleSelection(option.value)}
                startIcon={<ButtonIcon label={option.label}/>}
                key={option.value} value={option.value}
                variant={option.variant}
                sx={{borderTop: '0'}}
                color={buttonColor}
              >
                <Typography.Text>{option.label}</Typography.Text>
              </Button>
            ))}
          </ButtonGroup>
        )}
          <Popover
              content={menu}
              title="Select Year"
              trigger="hover"
            >
              <Button size={'small'} variant={'contained'} color={buttonColor} startIcon={<UnfoldMoreRounded/>}>Year</Button>
          </Popover>

      </Space>
      {extraMiddle && (
          extraMiddle
      )}
      <Space>
        {isLargeScreen ? <ChartConfigButtons chartConfig={chartConfig} setChartConfig={setChartConfig} extra={extra} /> : (
          <Popover content={
                    <ChartConfigButtons chartConfig={chartConfig} setChartConfig={setChartConfig} extra={extra} />
                   }
                   title="Additional Options"
                   trigger="hover">
            <Button size={'small'} startIcon={<MoreVertRounded/>} variant={'contained'} color={buttonColor}>Chart Tools</Button>
          </Popover>
        )}


      </Space>
    </Row>
  );
};

export default RangeSelector;
